import { useState } from 'react';
import {
  Alert,
  Button,
  TextField,
  Typography,
  InputLabel,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormGroup,
  FormHelperText,
} from '@mui/material';
import { FormikHelpers, useFormik } from 'formik';
import { isNil } from 'ramda';

import {
  attributesToSubmit,
  CreateOrganizationFormData,
  initialValues,
  getValidationSchema,
  CreateOrganizationFormIncludeFields,
} from 'src/forms/createOrganization';
import UsersPresenter from 'src/presenters/UsersPresenter';
import appRoutes from 'src/routes/appRoutes';
import { Address } from 'src/types/address';
import { Organization } from 'src/types/resources/Organization';
import {
  extractResponseErrors,
  isAxiosError,
  isUnprocessedEntityError,
  parseToFormikErrors,
} from 'src/utils/responseErrors';
import { ShowParams, UpdateParams } from 'src/repositories/superAdmin/OrganizationsRepository';
import useUsers from 'src/hooks/useUsers';
import useRouter from 'src/hooks/useRouter';
import useOrganizations from 'src/hooks/superAdmin/useOrganizations';
import useModals from 'src/hooks/useModals';
import useSnackbar from 'src/hooks/useSnackbar';
import AddressAutocomplete from 'src/components/AddressAutocomplete';
import Box from 'src/components/Box';
import Select from 'src/components/Select';
import DatePicker from 'src/components/DatePicker';
import { useFeatureFlags } from 'src/hooks';
import { AccountType, AccountTypeLabel } from 'src/enums/AccountType';

import styles from './styles';

type CreateOrganizationFormProps = {
  organization?: Organization;
  showOrganization: (params: ShowParams) => { unwrap: () => void };
  updateOrganization: (params: UpdateParams) => { unwrap: () => void };
};

const SELECT_ITEMS = [
  { value: AccountType.client, label: AccountTypeLabel.client },
  { value: AccountType.prospect, label: AccountTypeLabel.prospect },
  { value: AccountType.test, label: AccountTypeLabel.test },
];

const CreateOrganizationForm: React.FC<CreateOrganizationFormProps> = props => {
  const { featureShowOrgBrrPricingUpdate, featureUseW2EngagementTypeForOrg } = useFeatureFlags();
  const { organization, showOrganization, updateOrganization } = props;
  const [formError, setFormError] = useState<string | null>(null);
  const { currentUser } = useUsers();
  const { createOrganization } = useOrganizations();
  const { hideModal } = useModals();
  const { enqueueSuccessSnackbar } = useSnackbar();
  const { history } = useRouter();

  const isCreateMode = isNil(organization);
  const formTitle = isCreateMode ? 'Create Organization' : 'Edit Organization';
  const submitButtonText = isCreateMode ? 'Create Organization' : 'Save';
  const snackbarText = isCreateMode ? 'Organization was created' : 'Organization was updated';

  const licenseDateIsHidden = featureShowOrgBrrPricingUpdate && !isCreateMode;

  const accountTypeSelectorDisabled = featureShowOrgBrrPricingUpdate
    ? !isCreateMode && organization?.accountType !== AccountType.prospect
    : false;

  const hasSuperAdminAccess = UsersPresenter.hasSuperAdminAccess(currentUser);

  const getAccountTypeValuesForEdit = () => {
    if (!featureShowOrgBrrPricingUpdate) {
      return SELECT_ITEMS;
    }

    if (organization.accountType === AccountType.prospect) {
      return SELECT_ITEMS.filter(item => item.value !== AccountType.test);
    }

    return SELECT_ITEMS;
  };

  const handleSubmit = async (
    formData: CreateOrganizationFormData,
    { setErrors }: FormikHelpers<CreateOrganizationFormData>,
  ) => {
    const attributesToInclude: CreateOrganizationFormIncludeFields = [];

    if (
      formData.accountType === AccountType.client ||
      (formData.accountType === AccountType.test && featureShowOrgBrrPricingUpdate)
    ) {
      attributesToInclude.push('licenseDate');
    }

    if (formData.accountType === AccountType.client) {
      attributesToInclude.push('purchaseOrders');
    }

    const params = attributesToSubmit(formData, attributesToInclude);
    try {
      if (isCreateMode) {
        const { data: createdOrganization } = await createOrganization(params).unwrap();
        hideModal();
        history.push(appRoutes.superAdmin.organizationPath(createdOrganization.id), { activeTabIndex: 1 });
      } else {
        await updateOrganization({ id: organization.id, params }).unwrap();
        showOrganization({ id: organization.id });
      }
      enqueueSuccessSnackbar(snackbarText);
      hideModal();
    } catch (error) {
      if (isAxiosError(error) && isUnprocessedEntityError(error)) {
        const errors = extractResponseErrors(error);
        setFormError(error.response.data.errors.detail);
        setErrors(parseToFormikErrors(errors));
      }
    }
  };

  const formik = useFormik<CreateOrganizationFormData>({
    initialValues: initialValues({
      organization,
      featureShowOrgBrrPricingUpdate,
      featureUseW2EngagementTypeForOrg,
      userRole: currentUser.role,
    }),
    validationSchema: getValidationSchema({
      organization,
      featureShowOrgBrrPricingUpdate,
      featureUseW2EngagementTypeForOrg,
      userRole: currentUser.role,
    }),
    onSubmit: handleSubmit,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    setValues,
    setFieldValue,
    setFieldError,
    setFieldTouched,
    handleSubmit: handleFormikSubmit,
  } = formik;

  const handleAddressChange = (address: Address) => {
    setValues({
      ...values,
      address1: address.streetLine,
      address2: address.secondary,
      city: address.city,
      state: address.state,
      zipCode: address.zipcode,
    });
  };

  const isSelectedClientAccType = values.accountType === AccountType.client;

  return (
    <>
      {formError && <Alert severity="error">{formError}</Alert>}
      <Box sx={styles.modalWrapper}>
        <Typography variant="h3">{formTitle}</Typography>
        <form noValidate onSubmit={handleFormikSubmit}>
          {hasSuperAdminAccess && (
            <Box sx={styles.row}>
              <Select
                id="accountType"
                name="accountType"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.accountType}
                values={isCreateMode ? SELECT_ITEMS : getAccountTypeValuesForEdit()}
                error={touched.accountType && Boolean(errors.accountType)}
                helperText={touched.accountType && errors.accountType}
                disabled={accountTypeSelectorDisabled}
                label="Account Type"
              />
            </Box>
          )}

          <Box sx={styles.row}>
            <TextField
              id="name"
              label="Organization Name"
              type="text"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
            />
          </Box>

          {isSelectedClientAccType && hasSuperAdminAccess && (
            <Box sx={{ mb: 2 }}>
              <DatePicker
                value={values.contractExecutionDate || null}
                onChange={date => {
                  setFieldValue('contractExecutionDate', date);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Contract Execution Date"
                    sx={styles.dateField}
                    id="contractExecutionDate"
                    name="contractExecutionDate"
                    inputProps={{ ...params.inputProps, placeholder: 'Execution Date' }}
                    error={touched.contractExecutionDate && Boolean(errors.contractExecutionDate)}
                    helperText={touched.contractExecutionDate && errors.contractExecutionDate}
                    onBlur={handleBlur}
                  />
                )}
              />
            </Box>
          )}

          {!licenseDateIsHidden &&
            hasSuperAdminAccess &&
            (values.accountType === AccountType.client ||
              (values.accountType === AccountType.test && featureShowOrgBrrPricingUpdate)) && (
              <>
                <InputLabel
                  error={
                    (touched.licenseStartDate && Boolean(errors.licenseStartDate)) ||
                    (touched.licenseEndDate && Boolean(errors.licenseEndDate))
                  }
                >
                  License date
                </InputLabel>
                <Box sx={[styles.row, { mt: 0 }]}>
                  <Box sx={styles.dateRow}>
                    <DatePicker
                      value={values.licenseStartDate || null}
                      onChange={date => {
                        setFieldValue('licenseStartDate', date);
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          sx={styles.dateField}
                          id="licenseStartDate"
                          name="licenseStartDate"
                          inputProps={{ ...params.inputProps, placeholder: 'Start Date' }}
                          error={touched.licenseStartDate && Boolean(errors.licenseStartDate)}
                          helperText={touched.licenseStartDate && errors.licenseStartDate}
                          onBlur={handleBlur}
                        />
                      )}
                    />
                    <DatePicker
                      minDate={new Date(values.licenseStartDate)}
                      value={values.licenseEndDate || null}
                      onChange={date => {
                        setFieldValue('licenseEndDate', date);
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          sx={styles.dateField}
                          id="licenseEndDate"
                          name="licenseEndDate"
                          inputProps={{ ...params.inputProps, placeholder: 'End Date' }}
                          error={touched.licenseEndDate && Boolean(errors.licenseEndDate)}
                          helperText={touched.licenseEndDate && errors.licenseEndDate}
                          onBlur={handleBlur}
                        />
                      )}
                    />
                  </Box>
                </Box>
              </>
            )}
          {hasSuperAdminAccess && featureUseW2EngagementTypeForOrg && (
            <FormControl
              component="fieldset"
              variant="standard"
              sx={{ mb: 2 }}
              disabled={!!organization}
              error={!!errors.isW2Type && (touched.is1099Type || touched.isW2Type)}
            >
              <FormLabel component="legend">Engagement Type</FormLabel>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.is1099Type}
                      onChange={(e, checked) => {
                        setFieldTouched('is1099Type', true);
                        setFieldValue('is1099Type', checked);
                      }}
                      name="is1099Type"
                    />
                  }
                  sx={{ mr: 2 }}
                  label="1099"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isW2Type}
                      onChange={(e, checked) => {
                        setFieldTouched('isW2Type', true);
                        setFieldValue('isW2Type', checked);
                      }}
                      name="isW2Type"
                    />
                  }
                  label="W-2"
                />
              </FormGroup>
              <FormHelperText>
                {!!errors.isW2Type && (touched.is1099Type || touched.isW2Type) ? errors.isW2Type : null}
              </FormHelperText>
            </FormControl>
          )}
          {hasSuperAdminAccess && isSelectedClientAccType && (
            <Box sx={{ mb: 2 }}>
              <Typography fontWeight="600" mb="4px">
                Purchase Orders
              </Typography>
              <FormControlLabel
                label="Requires Purchase Orders on Engagements"
                control={
                  <Checkbox
                    color="primary"
                    checked={values.isEngagementRequiresPurchaseOrder}
                    onChange={date => {
                      setFieldValue('isEngagementRequiresPurchaseOrder', date.target.checked);
                    }}
                  />
                }
              />
            </Box>
          )}

          <Box sx={[styles.row, { mt: 0 }]}>
            <TextField
              id="website"
              label="Website"
              type="url"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.website}
              error={touched.website && Boolean(errors.website)}
              helperText={touched.website && errors.website}
            />
          </Box>
          <AddressAutocomplete
            initialValue={values.address1}
            onChangeAddress={handleAddressChange}
            setFieldValue={setFieldValue}
            setFieldError={setFieldError}
            setFieldTouched={setFieldTouched}
            error={touched.address1 && errors.address1}
          />
          <Box sx={styles.row}>
            <TextField
              id="address2"
              label="Address Line 2"
              type="text"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.address2}
              error={touched.address2 && Boolean(errors.address2)}
              helperText={touched.address2 && errors.address2}
            />
          </Box>
          <Box sx={styles.row}>
            <TextField
              id="city"
              label="City"
              type="text"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.city}
              error={touched.city && Boolean(errors.city)}
              helperText={touched.city && errors.city}
            />
          </Box>
          <Box sx={styles.row}>
            <TextField
              id="state"
              label="State"
              type="text"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.state}
              error={touched.state && Boolean(errors.state)}
              helperText={touched.state && errors.state}
              sx={{ mr: 2 }}
            />
            <TextField
              id="zipCode"
              label="Zip"
              type="text"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.zipCode}
              error={touched.zipCode && Boolean(errors.zipCode)}
              helperText={touched.zipCode && errors.zipCode}
              sx={styles.zipCode}
            />
          </Box>
          {hasSuperAdminAccess && (
            <Box sx={styles.row}>
              <TextField
                id="note"
                label="Note"
                multiline
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.note}
                error={touched.note && Boolean(errors.note)}
                helperText={touched.note && errors.note}
              />
            </Box>
          )}
          <Box sx={styles.buttons}>
            <Button type="submit" variant="contained" sx={styles.button} disabled={isSubmitting}>
              {submitButtonText}
            </Button>
            <Button variant="outlined" onClick={hideModal} sx={styles.button}>
              Cancel
            </Button>
          </Box>
        </form>
      </Box>
    </>
  );
};

export default CreateOrganizationForm;
